import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Coffee } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STRIPE_CONFIGURED, PLAN_BY_LOOKUP } from "@/lib/billing";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [state, setState] = useState("checking"); // checking|paid|failed|timeout
  const [plan, setPlan] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    const sid = params.get("session_id");
    const mockSid = params.get("mock_session_id");
    const lookupKey = params.get("lookup_key");

    // Dev mode: Stripe isn't configured, simulate successful checkout
    if (mockSid && lookupKey && !STRIPE_CONFIGURED) {
      // Simulate payment completion by directly activating the plan in dev
      (async () => {
        try {
          await api.get(`/payments/status/${mockSid}`);
          api.get("/auth/me").then(({ data: me }) => setUser(me)).catch(() => {});
          setPlan(PLAN_BY_LOOKUP.get(lookupKey, "trial"));
          setState("paid");
        } catch (e) {
          setState("failed");
        }
      })();
      return;
    }

    if (!sid) return setState("failed");
    let stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          // Refresh the cached user so the plan gate sees the new plan
          api.get("/auth/me").then(({ data: me }) => setUser(me)).catch(() => {});
          setPlan(data.plan);
          return setState("paid");
        }
        if (["failed", "expired"].includes(data.payment_status)) return setState("failed");
      } catch (e) {}
      attempts.current += 1;
      if (attempts.current >= 8) return setState("timeout");
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [params, setUser]);

  return (
    <div className="min-h-screen grid place-items-center bg-zinc-50 p-6">
      <div className="max-w-md w-full bg-white border border-zinc-200 rounded-xl p-10 text-center shadow-sm">
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className="h-8 w-8 rounded-lg bg-zinc-900 grid place-items-center"><Coffee className="h-4 w-4 text-white" /></div>
          <span className="font-display font-semibold">Optics Software</span>
        </div>
        {state === "checking" && (<><Loader2 className="h-10 w-10 mx-auto text-zinc-400 animate-spin" /><h1 className="font-display text-xl font-semibold mt-4">Confirming your payment…</h1><p className="text-sm text-zinc-500 mt-2">This only takes a moment.</p></>)}
        {state === "paid" && (<><CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500" /><h1 className="font-display text-2xl font-semibold mt-4">You're on {plan ? plan[0].toUpperCase() + plan.slice(1) : "your new plan"}!</h1><p className="text-sm text-zinc-500 mt-2">Subscription active. Welcome aboard.</p><button data-testid="go-dashboard" onClick={() => navigate("/app")} className="mt-6 bg-zinc-900 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-zinc-800 transition-colors duration-200">Go to dashboard</button></>)}
        {state === "timeout" && (<><Loader2 className="h-10 w-10 mx-auto text-amber-500" /><h1 className="font-display text-xl font-semibold mt-4">Still processing</h1><p className="text-sm text-zinc-500 mt-2">Your payment is taking longer than usual. It will update shortly.</p><button onClick={() => navigate("/app/settings")} className="mt-6 border border-zinc-200 px-6 py-2.5 rounded-lg font-medium hover:bg-zinc-50">Back to settings</button></>)}
        {state === "failed" && (<><XCircle className="h-12 w-12 mx-auto text-red-500" /><h1 className="font-display text-xl font-semibold mt-4">Payment not completed</h1><p className="text-sm text-zinc-500 mt-2">No charge was made. You can try again anytime.</p><Link to="/app/settings" className="mt-6 inline-block border border-zinc-200 px-6 py-2.5 rounded-lg font-medium hover:bg-zinc-50">Back to settings</Link></>)}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen grid place-items-center bg-zinc-50 p-6">
      <div className="max-w-md w-full bg-white border border-zinc-200 rounded-xl p-10 text-center shadow-sm">
        <XCircle className="h-12 w-12 mx-auto text-zinc-400" />
        <h1 className="font-display text-xl font-semibold mt-4">Checkout cancelled</h1>
        <p className="text-sm text-zinc-500 mt-2">No charge was made. Your plan is unchanged.</p>
        <button data-testid="cancel-back" onClick={() => navigate("/app/settings")} className="mt-6 bg-zinc-900 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-zinc-800 transition-colors duration-200">Back to settings</button>
      </div>
    </div>
  );
}
